/**
 * Trash (FR-DEL-01, FR-DEL-04).
 *
 * Reading what is in the bin and putting things back. **Destroying is not here** — the
 * purge is a scheduled job running as the service account (FR-DEL-05, `server/`), and
 * the client rules deny hard delete on every collection, so this module could not purge
 * anything even if it tried.
 */

import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import type { DateKey } from '../core/dates';
import { purgeAfterFor, type Trashed } from '../core/trash';
import { toTransactionList } from './transactions';
import { db } from './firebase';

/**
 * Soft-deleted transactions.
 *
 * Only transactions today: they are the only collection with a soft-delete path, because
 * accounts and categories archive rather than delete (FR-ACC-03, FR-CAT-03) and the rest
 * deny deletion outright. The shape is general so the screen does not change when another
 * collection joins.
 */
export function watchTrash(onChange: (items: Trashed[]) => void): () => void {
  return onSnapshot(
    query(collection(db, 'transactions'), where('deleted', '==', true)),
    (snapshot) => onChange(snapshot.docs.map(toTrashed)),
    () => onChange([]),
  );
}

function toTrashed(document: { id: string; data: () => Record<string, unknown> }): Trashed {
  const data = document.data();
  const deletedOn =
    (data.deletedAt as { toDate?: () => Date })?.toDate?.()?.toISOString().slice(0, 10) ??
    (data.dateKey as DateKey) ??
    '';

  return {
    id: document.id,
    collection: 'transactions',
    label: (data.description as string) || (data.dateKey as string) || document.id,
    deletedOn,
    // Read, never recomputed. A record whose stamp is missing is kept rather than
    // treated as ancient — `duePurge` refuses to touch it (core/trash.ts).
    purgeAfter: (data.purgeAfter as DateKey) ?? (deletedOn ? purgeAfterFor(deletedOn) : ''),
    attachments: ((data.attachments as { path: string }[]) ?? []).map((a) => a.path),
    amountPaise: (data.amountPaise as number) ?? undefined,
  };
}

/** The full transaction behind a trashed row, for the restore path. */
export async function loadTrashedTransactions() {
  const snapshot = await getDocs(
    query(collection(db, 'transactions'), where('deleted', '==', true)),
  );
  return toTransactionList(snapshot.docs);
}
